import { RoomOutlined } from '@mui/icons-material';
import { Box, Chip } from '@mui/material';
import React from 'react';

import { useAuth } from '../../hooks/useAuth';
import type { GeographicRestrictions } from '../../types/pricing';

interface GeographicRestrictionsBannerProps {
  restrictions: GeographicRestrictions;
  className?: string;
}

/**
 * Geographic Restrictions Banner
 *
 * Logic:
 * - type === 'everywhere' → render nothing (no restrictions)
 * - User logged in → render nothing (API already filtered; if service is visible, address is in zone)
 * - User NOT logged in → show a small chip: "Add address to see availability" → /register
 *
 * We never render the state/county/zip list — it's noisy and meaningless to the buyer.
 * The API handles filtering for authenticated users. Unauthenticated users see a
 * lightweight CTA to create a free account so availability can be determined.
 */
export const GeographicRestrictionsBanner: React.FC<GeographicRestrictionsBannerProps> = ({
  restrictions,
  className,
}) => {
  const { isAuthenticated } = useAuth();

  // No restrictions — render nothing
  if (!restrictions || restrictions.type === 'everywhere') {
    return null;
  }

  // Logged-in users: API has already filtered. If the card is visible, they're in zone.
  // Render nothing — don't clutter the card with redundant info.
  if (isAuthenticated) {
    return null;
  }

  // Not logged in: show a clean, low-friction CTA chip
  return (
    <Box className={className} sx={{ mb: 1.5 }}>
      <Chip
        icon={<RoomOutlined sx={{ fontSize: '0.85rem !important' }} />}
        label="Add address to see availability"
        size="small"
        variant="outlined"
        color="default"
        component="a"
        href="/register"
        clickable
        sx={{
          fontSize: '0.75rem',
          height: '26px',
          cursor: 'pointer',
          borderColor: 'divider',
          color: 'text.secondary',
          '&:hover': {
            borderColor: 'primary.main',
            color: 'primary.main',
            bgcolor: 'primary.50',
          },
        }}
      />
    </Box>
  );
};